import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Upload, X, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth check hook for support pages
const useAuthCheck = (navigate) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      
      if (!token || !userInfo) {
        toast.error('Please login to access the Support Dashboard');
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userInfo);
        if (user.role !== 'internal_team' && user.role !== 'admin') {
          toast.error('Access denied. Internal team role required.');
          navigate('/login');
          return;
        }
        setIsAuthorized(true);
      } catch (e) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  return { isAuthorized, isChecking };
};

const issueCategories = [
  { value: 'unable_to_load', label: 'Unable to Load Assessment' },
  { value: 'answers_wiped', label: 'Answers/Progress Wiped' },
  { value: 'email_not_received', label: 'Email Not Received' },
  { value: 'wrong_assessment_content', label: 'Wrong Assessment Content' },
  { value: 'login_error', label: 'Login Error' },
  { value: 'progress_not_saving', label: 'Progress Not Saving' },
  { value: 'display_error', label: 'Display/UI Error' },
  { value: 'missing_from_system', label: 'Missing from System' },
  { value: 'other', label: 'Other' }
];

const regions = [
  { value: 'usa', label: '🇺🇸 USA' },
  { value: 'canada', label: '🇨🇦 Canada' },
  { value: 'jamaica', label: '🇯🇲 Jamaica' },
  { value: 'barbados', label: '🇧🇧 Barbados' },
  { value: 'trinidad_tobago', label: '🇹🇹 Trinidad & Tobago' },
  { value: 'panama', label: '🇵🇦 Panama' }
];

export default function IssueReportForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthorized, isChecking } = useAuthCheck(navigate);
  const prefillData = location.state || {};

  const [formData, setFormData] = useState({
    employee_name: prefillData.employeeName || '',
    employee_email: prefillData.employeeEmail || '',
    employee_role: prefillData.employeeRole || '',
    employee_region: prefillData.employeeRegion || '',
    assessment_link: prefillData.assessmentLink || '',
    candidate_id: prefillData.candidateId || '',
    issue_category: '',
    issue_description: ''
  });

  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't render if not authorized (will redirect)
  if (!isAuthorized) {
    return null;
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(file.type)) {
      toast.error('Please upload a PNG, JPG, or GIF image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setScreenshotPreview(event.target.result);
      setScreenshot({
        data: event.target.result,
        filename: file.name
      });
    };
    reader.readAsDataURL(file);
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employee_name || !formData.issue_category || !formData.issue_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        screenshot_data: screenshot?.data || null,
        screenshot_filename: screenshot?.filename || null
      };

      const response = await fetch(`${API_URL}/api/support/issues`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Issue submitted! Auto-diagnosis started.');
        navigate(`/support/issues/${data.issueId}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit issue');
      }
    } catch (error) {
      console.error('Error submitting issue:', error);
      toast.error('Failed to submit issue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="issue-report-form">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/support')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Report an Issue</h1>
          <p className="text-gray-600 mt-1">Submit a support issue for auto-diagnosis and resolution</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Employee Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Employee Information</CardTitle>
              <CardDescription>Details about the affected Sagicor team member</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_name">Employee Name *</Label>
                  <Input
                    id="employee_name"
                    value={formData.employee_name}
                    onChange={(e) => handleInputChange('employee_name', e.target.value)}
                    placeholder="Enter employee name"
                    required
                    data-testid="employee-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employee_email">Employee Email</Label>
                  <Input
                    id="employee_email"
                    type="email"
                    value={formData.employee_email}
                    onChange={(e) => handleInputChange('employee_email', e.target.value)}
                    placeholder="employee@sagicor.com"
                    data-testid="employee-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employee_role">Role/Title</Label>
                  <Input
                    id="employee_role"
                    value={formData.employee_role}
                    onChange={(e) => handleInputChange('employee_role', e.target.value)}
                    placeholder="e.g., Software Engineer"
                    data-testid="employee-role-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employee_region">Region</Label>
                  <Select
                    value={formData.employee_region}
                    onValueChange={(v) => handleInputChange('employee_region', v)}
                  >
                    <SelectTrigger data-testid="region-select">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assessment_link">Assessment Link</Label>
                <Input
                  id="assessment_link"
                  value={formData.assessment_link}
                  onChange={(e) => handleInputChange('assessment_link', e.target.value)}
                  placeholder="/assessment/xxxxx-xxxxx-xxxxx"
                  data-testid="assessment-link-input"
                />
                <p className="text-xs text-gray-500">The unique assessment URL for this employee</p>
              </div>
            </CardContent>
          </Card>

          {/* Issue Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Issue Details</CardTitle>
              <CardDescription>Describe the problem that was reported</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="issue_category">Issue Category *</Label>
                <Select
                  value={formData.issue_category}
                  onValueChange={(v) => handleInputChange('issue_category', v)}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select issue category" />
                  </SelectTrigger>
                  <SelectContent>
                    {issueCategories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="issue_description">Issue Description *</Label>
                <Textarea
                  id="issue_description"
                  value={formData.issue_description}
                  onChange={(e) => handleInputChange('issue_description', e.target.value)}
                  placeholder="Describe what the employee reported. Include any error messages they saw, steps they took, etc."
                  rows={5}
                  required
                  data-testid="description-textarea"
                />
              </div>

              {/* Screenshot Upload */}
              <div className="space-y-2">
                <Label>Screenshot (Optional)</Label>
                {!screenshotPreview ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-gray-300 transition-colors">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif"
                      onChange={handleScreenshotUpload}
                      className="hidden"
                      id="screenshot-upload"
                      data-testid="screenshot-upload"
                    />
                    <label htmlFor="screenshot-upload" className="cursor-pointer">
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">Click to upload a screenshot</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                    </label>
                  </div>
                ) : (
                  <div className="relative border rounded-lg p-2">
                    <img 
                      src={screenshotPreview} 
                      alt="Screenshot preview" 
                      className="max-h-64 mx-auto rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeScreenshot}
                      data-testid="remove-screenshot"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-2">{screenshot?.filename}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/support')}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Issue & Start Auto-Diagnosis
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
